import { Inject, Injectable } from '@angular/core';
import { File } from '@awesome-cordova-plugins/file/ngx';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { Mutex } from 'async-mutex';
import moment from 'moment';
import { BehaviorSubject, Observable, combineLatest, forkJoin, from, of, throwError } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, mergeMap, take, tap } from 'rxjs/operators';
import { CachedPayload } from '../../classes/cached-request';
import { OfflineStrategy } from '../../interfaces/offline-strategy.interface';
import { selectOfflineCache } from '../../store/selectors/cache.selector';
import { selectRequestQueue } from '../../store/selectors/request-queue.selector';
import { PaceOfflineFeatureState } from '../../store/state';
import { CacheState } from '../../store/types/cache.state';
import { RequestQueueState } from '../../store/types/request-queue.state';
import { isFileError } from '../../util/is-file-error';

const KEY_OFFLINE_CACHE = 'offlineCache';
const KEY_OFFLINE_REQUEST_QUEUE = 'offlineRequestQueue';

@Injectable()
export class FileStorageOfflineStrategyService implements OfflineStrategy {
    private offlineCacheMutex = new Mutex();
    private offlineRequestQueueMutex = new Mutex();

    private offlineCacheChangedNotYetSavedSubject = new BehaviorSubject(false);
    private offlineCacheChangedNotYetSaved$ = this.offlineCacheChangedNotYetSavedSubject.asObservable();
    private requestQueueStateChangedNotYetSavedSubject = new BehaviorSubject(false);
    private requestQueueStateChangedNotYetSaved$ = this.requestQueueStateChangedNotYetSavedSubject.asObservable();
    public constructor(
        @Inject(LogInstanceToken) private log: LogInterface,
        private readonly cordovaFile: File,
        private readonly store: Store<PaceOfflineFeatureState>,
    ) {
        this.store
            .select(selectOfflineCache)
            .pipe(
                distinctUntilChanged(),
                // this is to indicate that OfflineCache state has changed, but not saved to file yet.
                tap(() => this.offlineCacheChangedNotYetSavedSubject.next(true)),
            )
            .subscribe();

        this.store
            .select(selectRequestQueue)
            .pipe(
                distinctUntilChanged(),
                tap((requests) => {
                    this.log.info(
                        `FileStorageOfflineStrategyService: selectRequestQueue: total ${requests.length} pending requests`,
                    );
                    // this is to indicate that RequestQueueState state has changed, but not saved to file yet.
                    this.requestQueueStateChangedNotYetSavedSubject.next(true);
                }),
            )
            .subscribe();
    }

    public getState(): Observable<PaceOfflineFeatureState | undefined> {
        return forkJoin([
            this.readData(KEY_OFFLINE_CACHE, this.offlineCacheMutex).pipe(
                map((data: string) => JSON.parse(data) as CacheState),
            ),
            this.readData(KEY_OFFLINE_REQUEST_QUEUE, this.offlineRequestQueueMutex).pipe(
                map((data: string) => JSON.parse(data) as RequestQueueState),
            ),
        ]).pipe(
            map(([cacheState, requestQueueState]) => {
                return {
                    cache: cacheState,
                    requestQueue: requestQueueState,
                } as PaceOfflineFeatureState;
            }),
            catchError((error) => {
                this.log.error(
                    `FileStorageOfflineStrategyService: getState: error: ${JSON.stringify(
                        error,
                        Object.getOwnPropertyNames(error),
                    )}`,
                );
                return of(undefined);
            }),
        );
    }

    /**
     * 1. Save the changes of only RequestQueueState and OfflineCache; others like RefreshRequestQueueState do not need to be saved to file storage.
     * 2. When there is any change in PaceOfflineFeatureState this method is called from inside a switchMap
     * which means it may get unsubscribed before all data gets saved, when a subsequent call is made.
     * For example: a post request was added to requestQueue, and immediately a GET request gets added to refreshRequestQueue
     * In this case when setState was called first time, it had to write requestQueueState to fileStorage
     * but the write operation could not complete as the change in refreshRequestQueueState made this method to be called again.
     * So on each call, check again if the RequestQueueState or OfflineCache state changes are not yet saved to file storage.
     *
     * @param state the state to be set
     * @returns Observable<boolean> indicating success or failure of the operation
     */
    public setState(state: PaceOfflineFeatureState): Observable<boolean> {
        const startTimer = moment();
        // OfflineCacheState change can occur very often, i.e within milliseconds
        // It is not necessary to write to file storage each time for OfflineCache,
        // hence a debounce of 3 seconds have been used after which the latest state of offlineCache will be saved
        const debounceTimeinMs = 3000;
        return combineLatest([
            this.requestQueueStateChangedNotYetSaved$,
            this.offlineCacheChangedNotYetSaved$.pipe(debounceTime(debounceTimeinMs)),
        ]).pipe(
            take(1),
            filter(
                ([hasRequestQueueStateChanged, hasOfflineCacheChanged]) =>
                    hasRequestQueueStateChanged || hasOfflineCacheChanged,
            ),
            mergeMap(([hasRequestQueueStateChanged, hasOfflineCacheChanged]) => {
                this.log.debug('hasRequestQueueStateChanged: ', `${hasRequestQueueStateChanged}`);
                this.log.debug('hasOfflineCacheChanged: ', `${hasOfflineCacheChanged}`);
                const stateChange$ = [];
                if (hasRequestQueueStateChanged) {
                    this.log.info(
                        `FileStorageOfflineStrategyService: setState: total ${state.requestQueue.requests.length} pending requests`,
                    );
                    state.requestQueue.requests.forEach((request) =>
                        this.log.info(
                            `${request.id} ${
                                request.url.toLowerCase().indexOf('deepinsert') > 0
                                    ? (<CachedPayload>request.body)?.Endpoint
                                    : request.url
                            }`,
                        ),
                    );
                    stateChange$.push(
                        this.writeData(
                            KEY_OFFLINE_REQUEST_QUEUE,
                            JSON.stringify(state.requestQueue),
                            this.offlineRequestQueueMutex,
                        ).pipe(
                            tap(() => {
                                this.log.debug(
                                    'FileStorageOfflineStrategyService: hasRequestQueueStateChanged: setting to false',
                                );
                                // reset to false only after requestQueueState change has been saved.
                                this.requestQueueStateChangedNotYetSavedSubject.next(false);
                            }),
                        ),
                    );
                }

                if (hasOfflineCacheChanged) {
                    stateChange$.push(
                        this.writeData(KEY_OFFLINE_CACHE, JSON.stringify(state.cache), this.offlineCacheMutex).pipe(
                            tap(() => {
                                this.log.debug(
                                    'FileStorageOfflineStrategyService: hasOfflineCacheChanged: setting to false',
                                );
                                // reset to false only after offlineCache change has been saved.
                                this.offlineCacheChangedNotYetSavedSubject.next(false);
                            }),
                        ),
                    );
                }
                return forkJoin(...stateChange$);
            }),
            map(() => {
                this.log.debug(
                    `FileStorageOfflineStrategyService: setState: total time taken: ${moment().diff(
                        startTimer,
                        'millisecond',
                    )}ms`,
                );
                return true;
            }),
            catchError((error) => {
                this.log.error(
                    `FileStorageOfflineStrategyService: setState: error: ${JSON.stringify(
                        error,
                        Object.getOwnPropertyNames(error),
                    )}`,
                );
                return throwError(error);
            }),
        );
    }

    public deleteCache(): Observable<boolean> {
        const startTimer = moment();
        return from(this.removeFile()).pipe(
            tap(() =>
                this.log.debug(
                    `FileStorageOfflineStrategyService: deleteCache: time taken: ${moment().diff(
                        startTimer,
                        'millisecond',
                    )}ms`,
                ),
            ),

            map((result) => typeof result !== 'undefined' || typeof result !== null),
            catchError((error) => {
                this.log.error(
                    `FileStorageOfflineStrategyService: deleteCache: error: ${JSON.stringify(
                        error,
                        Object.getOwnPropertyNames(error),
                    )}`,
                );
                return of(false);
            }),
        );
    }

    private readData(fileName: string, mutex: Mutex): Observable<string> {
        return from(this.readAsText(fileName, mutex));
    }

    private async readAsText(fileName: string, mutex: Mutex): Promise<string> {
        const unlock = await mutex.acquire();
        return this.cordovaFile.readAsText(this.cordovaFile.dataDirectory, fileName).finally(() => {
            unlock();
        });
    }

    private writeData(fileName: string, content: string, mutex: Mutex): Observable<void> {
        const startTimer = moment();
        return from(this.writeFile(fileName, content, mutex)).pipe(
            tap(() =>
                this.log.debug(
                    `FileStorageOfflineStrategyService: writeData to file '${fileName}'.
                     Data size: ${content.length} chars; time taken: ${moment().diff(startTimer, 'millisecond')}ms`,
                ),
            ),
            catchError((error) => {
                this.log.error(
                    `FileStorageOfflineStrategyService: writeData: error: ${JSON.stringify(
                        error,
                        Object.getOwnPropertyNames(error),
                    )} for file '${fileName}'`,
                );
                return throwError(error);
            }),
        );
    }

    private async writeFile(filename: string, content: string, mutex: Mutex): Promise<void> {
        const unlock = await mutex.acquire();
        this.log.debug(`FileStorageOfflineStrategyService: writeFile: locking '${filename}' for saving data`);
        return this.cordovaFile
            .writeFile(this.cordovaFile.dataDirectory, filename, content, {
                replace: true,
            })
            .finally(() => {
                this.log.debug(`FileStorageOfflineStrategyService: writeFile: unlocking ${filename}`);
                unlock();
            });
    }

    private async removeFile(): Promise<void> {
        const unlock = await this.offlineCacheMutex.acquire();
        this.log.debug(`FileStorageOfflineStrategyService: removeFile: locking ${KEY_OFFLINE_CACHE} for removing data`);
        return this.cordovaFile
            .removeFile(this.cordovaFile.dataDirectory, KEY_OFFLINE_CACHE)
            .then(() => undefined)
            .catch((error: unknown) => {
                // if the file was not cached, ignore the NOT_FOUND_ERR (=> 1)
                const suppressError = isFileError(error) && error.code === 1;
                if (suppressError) {
                    return;
                }
                throw error;
            })
            .finally(() => {
                this.log.debug(`FileStorageOfflineStrategyService: removeFile: unlocking ${KEY_OFFLINE_CACHE}`);
                unlock();
            });
    }
}
